import {computed, Ref, ref, UnwrapRef, watch} from "@vue/composition-api";
import {BTable, BvTableField} from "bootstrap-vue";
import {useInertia} from "@tool/Helpers/useInertia";
import {ResourceCollectionInterface} from "@base/base.types";

interface TableFieldInterface extends BvTableField {
    key: string;
}

type DataTableState = 'loaded' | 'empty' | 'empty-search';


export type useInertiaTable<TData> = Ref<{
    fields: BvTableField[];

    data: UnwrapRef<ResourceCollectionInterface<TData>>;

    filter: string;

    isFinishedLoading: boolean;
    isBusy: boolean;

    infoTotalRows: number;
    infoCurrentPage: number;
    infoFrom: number;
    infoTo: number;

    optionPerPage: number;
    optionPerPageOptions: number[];
    optionSortBy: string;
    optionSortDesc: boolean;
    optionSortDirection: 'desc' | 'asc';

    showFooterPagination: boolean;

    props: unknown;
}>;

export default function useInertiaTable<TData>(
    data: ResourceCollectionInterface<TData>,
    fields: TableFieldInterface[]
): useInertiaTable<TData> {
    const refTable = ref<null | BTable>(null);

    const urlParams = new URLSearchParams(window.location.search);

    const filter = ref(urlParams.get('search') ?? '');

    const isFinishedLoading = ref(false);
    const initialInfoTotalRows = ref<null | number>(null);
    const isBusy = ref(false);

    const infoTotalRows = ref(data.meta.total);
    const infoCurrentPage = ref(data.meta.current_page);
    const infoFrom = ref(data.meta.from);
    const infoTo = ref(data.meta.to);

    const optionPerPage = ref(data.meta.per_page ?? 25);
    const defaultPerPageOption = 25;
    const optionPerPageOptions = ref([10, 25, 50, 100]);
    const optionSortBy = ref('');
    const optionSortDesc = ref(false);

    /**
     *  Control the order in which ascending and descending sorting is applied
     *  when a sortable column header is clicked, by using the sort-direction prop.
     */
    const optionSortDirection = ref<'desc' | 'asc'>('desc');

    const state = computed<DataTableState>(() => {

        if (infoTotalRows.value === 0) {
            if (filter.value !== '') {
                return 'empty-search';
            }
            return 'empty';
        }

        return 'loaded';
    });

    const showFooterPagination = computed(() => {
        return initialInfoTotalRows.value !== 0 && infoTotalRows.value > optionPerPage.value;
    });
    /**
     * FetchItems is not optimal because I cannot directly modify items
     * https://bootstrap-vue.org/docs/components/table#using-items-provider-functions
     * Must return null
     * @param ctx
     * @param callback
     */
    // const fetchItems = (ctx: BvTableCtxObject, callback: ([]) => Array<any>) => {
    //
    //     /**
    //      * On first load do nothing
    //      */
    //     if (isFinishedLoading.value === false) {
    //         // @ts-ignore
    //         callback(data.data);
    //         isFinishedLoading.value = true;
    //         return null;
    //     }
    //
    //     const params = {
    //         ...route().params, ...{
    //             page: infoCurrentPage.value > 1 ? infoCurrentPage.value : undefined,
    //             search: filter.value !== '' ? filter.value : undefined
    //         }
    //     }
    //
    //     // @ts-ignore
    //     // noinspection JSIgnoredPromiseFromCall
    //     inertia.get(route(route().current(), params), {}, {
    //         preserveScroll: true,
    //     });
    //
    //
    //     return null;
    // }

    watch([infoCurrentPage, filter], (current, old) => {
        if (current[1] !== old[1]) {
            if (infoCurrentPage.value !== 1) {
                infoCurrentPage.value = 1;
                // This watch is going to be triggered again
                return;
            }
        }
        isBusy.value = true;
        const params = {
            ...route().params, ...{
                page: infoCurrentPage.value > 1 ? infoCurrentPage.value : undefined,
                search: filter.value !== '' ? filter.value : undefined
            }
        }
        // @ts-ignore
        // noinspection JSIgnoredPromiseFromCall
        useInertia().get(route(route().current(), params), {}, {
            preserveScroll: true,
        });
    })

    const props = {
        ref: refTable,
        fields,
        items: data.data,
        // 'current-page': infoCurrentPage,
        // 'per-page': optionPerPage,
        // 'sort-by': optionSortBy,
        // 'sort-desc': optionSortDesc,
        'responsive': true,
        // 'striped': true,
        'hover': true,
        'borderless': true,
        // 'thead-class': 'thead-light',
        filter,
        busy: isBusy
    }

    return ref({
        props,

        refTable,

        data,

        state,

        fields,

        filter,

        isFinishedLoading,
        isBusy,

        infoTotalRows,
        infoCurrentPage,
        infoFrom,
        infoTo,

        optionPerPage,
        optionPerPageOptions,
        optionSortBy,
        optionSortDesc,
        optionSortDirection,

        showFooterPagination,

    });
}